import Vue from "vue"
import api from "@/api"
import { store } from "@/store/stores"
import userService from "@/services/userService"
import { currentWatchPlatform } from "@/services/watchPlatform"
import trainingLiveService from "./trainingLiveService"
import trainingFeatureAccess from "./trainingFeatureAccess"

const WATCH_SCHEMA_VERSION = 1
const MAX_WATCH_PLANS = 30
const MAX_EXERCISES_PER_PLAN = 150
const MAX_SETS_PER_EXERCISE = 30
const MAX_WATCH_STATISTIC_EXERCISES = 500
const MAX_WATCH_LAST_SETS = 8
const MAX_WATCH_RECENT_SESSIONS = 5
const MAX_WATCH_GROUP_FITNESS_ACTIVITIES = 20
const WATCH_EXERCISE_IMAGE_BASE_URL = "https://app.wellcomefit.ch/img/uebungen/"

function watchDiagnostic(event, details = {}) {
  if (!trainingFeatureAccess.isAllowed()) return
  console.info(`[WatchSync] ${event}`, details)

  try {
    const bridge = window?.flutter_inappwebview
    if (bridge && typeof bridge.callHandler === "function") {
      const safeDetails = {}
      Object.entries(details)
        .slice(0, 12)
        .forEach(([key, value]) => {
          if (typeof value === "boolean" || typeof value === "number" || value === null) {
            safeDetails[key] = value
          } else if (typeof value === "string") {
            safeDetails[key] = value.slice(0, 80)
          }
        })
      Promise.resolve(
        bridge.callHandler("watchDiagnosticLog", {
          event,
          details: safeDetails,
        }),
      ).catch(() => {})
    }
  } catch (_) {
    // Diagnose darf die Synchronisierung niemals beeinflussen.
  }
}

function watchErrorDetails(error) {
  return {
    status: Number(error?.response?.status) || null,
    code: error?.code ? String(error.code).slice(0, 64) : null,
    name: error?.name ? String(error.name).slice(0, 64) : null,
    serverCode: error?.response?.data?.code ? String(error.response.data.code).slice(0, 64) : null,
    message: watchTransferErrorMessage(error),
  }
}

function watchTransferErrorMessage(
  error,
  fallback = "Das Training konnte noch nicht im Trainingshub gespeichert werden.",
) {
  const rawMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback
  const message = String(rawMessage).replace(/\s+/g, " ").trim()
  return (message || fallback).slice(0, 240)
}

function idString(value) {
  if (!value) return ""
  if (value._id) return String(value._id)
  return String(value)
}

function cleanText(value, fallback = "") {
  const text = String(value || fallback).trim()
  return text.slice(0, 160)
}

function finiteNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function isoDateWithoutMilliseconds(date = new Date()) {
  return date.toISOString().replace(/\.\d{3}Z$/, "Z")
}

function safeIsoDate(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : isoDateWithoutMilliseconds(date)
}

function boundedPositiveNumber(value, maximum, integer = false) {
  const number = finiteNumber(value)
  if (number === null || number <= 0) return null
  const bounded = Math.min(number, maximum)
  return integer ? Math.round(bounded) : bounded
}

function boundedCount(value) {
  const number = finiteNumber(value)
  if (number === null) return 0
  return Math.min(1000000, Math.max(0, Math.round(number)))
}

function watchExerciseImageUrl(exercise) {
  const rawPicture = exercise?.picture || exercise?.manual?.[0]?.picture
  if (typeof rawPicture !== "string") return null

  const rawValue = rawPicture.trim()
  if (!rawValue || rawValue.length > 768) return null

  try {
    let url
    if (/^https?:\/\//i.test(rawValue)) {
      url = new URL(rawValue)
    } else {
      let relativePath = rawValue.replace(/\\/g, "/").replace(/^\/+/, "")
      relativePath = relativePath.replace(/^img\/uebungen\//i, "")
      const segments = relativePath.split("/")
      if (segments.some((segment) => !segment || segment === "." || segment === "..")) return null
      url = new URL(segments.map((segment) => encodeURIComponent(segment)).join("/"), WATCH_EXERCISE_IMAGE_BASE_URL)
    }

    if (
      url.protocol !== "https:" ||
      url.hostname.toLowerCase() !== "app.wellcomefit.ch" ||
      url.port ||
      url.username ||
      url.password ||
      !url.pathname.startsWith("/img/uebungen/")
    ) {
      return null
    }

    url.search = ""
    url.hash = ""
    const value = url.toString()
    return value.length <= 1024 ? value : null
  } catch (_) {
    return null
  }
}

function watchPlanIconUrl(split) {
  // Use the saved split artwork, never the first exercise photo. These are
  // the same assets and title fallbacks used by the training-plan chooser.
  const saved = typeof split?.fixedVisualization?.img === "string" ? split.fixedVisualization.img.trim() : ""
  if (/^(?:\/img\/workout\/[a-zA-Z0-9_-]+\.(?:png|webp|jpe?g)|\/img\/hyrox_logo\.png)$/.test(saved)) {
    return `https://app.wellcomefit.ch${saved}`
  }
  const title = String(split?.title || "").toLowerCase()
  if (title.includes("hyrox")) return "https://app.wellcomefit.ch/img/hyrox_logo.png"
  const options = [
    [["bein", "beine", "unterkörper", "leg", "lower body"], "legSplit.png"],
    [["brust", "push", "chest"], "chestSplit.png"],
    [["rücken", "pull", "schulter", "back", "shoulder"], "backSplit.png"],
    [["arm", "arme", "bizeps", "trizeps"], "armSplit.png"],
    [["bauch", "core", "ober"], "cardioSplit.png"],
  ]
  const match = options.find(([words]) => words.some(word => title.includes(word)))
  return `https://app.wellcomefit.ch/img/workout/${match ? match[1] : "upperBodySplit.png"}`
}

function watchGroupFitnessImageUrl(value) {
  if (typeof value !== "string") return null
  const rawValue = value.trim()
  if (!rawValue || rawValue.length > 1024) return null

  try {
    const url = new URL(rawValue, "https://app.wellcomefit.ch")
    if (
      url.protocol !== "https:" ||
      url.hostname.toLowerCase() !== "app.wellcomefit.ch" ||
      url.port ||
      url.username ||
      url.password ||
      !url.pathname.startsWith("/img/kurse/")
    ) {
      return null
    }

    url.search = ""
    url.hash = ""
    return url.toString()
  } catch (_) {
    return null
  }
}

function normalizeWatchGroupFitnessActivity(rawActivity) {
  if (!rawActivity || typeof rawActivity !== "object" || Array.isArray(rawActivity)) return null

  const id = idString(rawActivity.id || rawActivity._id)
  const title = cleanText(rawActivity.title)
  const startAt = safeIsoDate(rawActivity.startAt)
  if (!id || !title || !startAt) return null

  const durationMinutes = boundedPositiveNumber(rawActivity.durationMinutes, 24 * 60, true)
  const instructorNames = (Array.isArray(rawActivity.instructorNames) ? rawActivity.instructorNames : [])
    .map((name) => cleanText(name, ""))
    .filter(Boolean)
    .slice(0, 6)
  const rawColor = String(rawActivity.colorHex || "").trim()
  const allowedHealthTypes = new Set([
    "cycling",
    "running",
    "walking",
    "yoga",
    "dance",
    "highIntensityIntervalTraining",
    "functionalStrengthTraining",
    "traditionalStrengthTraining",
    "mindAndBody",
    "flexibility",
    "crossTraining",
    "mixedCardio",
  ])
  const healthActivityType = allowedHealthTypes.has(rawActivity.healthActivityType)
    ? rawActivity.healthActivityType
    : "mixedCardio"

  return {
    id,
    title,
    subtitle: cleanText(rawActivity.subtitle) || null,
    imageUrl: watchGroupFitnessImageUrl(rawActivity.imageUrl),
    startAt,
    durationMinutes,
    studioName: cleanText(rawActivity.studioName) || null,
    instructorNames,
    colorHex: /^#[0-9a-f]{6}$/i.test(rawColor) ? rawColor : "#00963F",
    healthActivityType,
  }
}

function normalizeWatchPerformanceSet(rawSet, index) {
  const rawPosition = finiteNumber(rawSet?.position)
  const position = rawPosition !== null && rawPosition > 0 ? Math.min(10000, Math.round(rawPosition)) : index + 1

  return {
    position,
    reps: boundedPositiveNumber(rawSet?.reps, 10000, true),
    weightKg: boundedPositiveNumber(rawSet?.weightKg, 2000),
    durationSeconds: boundedPositiveNumber(rawSet?.durationSeconds, 86400, true),
    distanceMeters: boundedPositiveNumber(rawSet?.distanceMeters, 10000000),
  }
}

function normalizeWatchExerciseHistoryEntry(rawEntry, index) {
  if (!rawEntry || typeof rawEntry !== "object" || Array.isArray(rawEntry)) return null

  const sets = (Array.isArray(rawEntry.sets) ? rawEntry.sets : [])
    .slice(0, MAX_WATCH_LAST_SETS)
    .map(normalizeWatchPerformanceSet)
  const performedAt = safeIsoDate(rawEntry.performedAt)
  if (!performedAt && sets.length === 0) return null

  return {
    id: cleanText(rawEntry.id, `watch-history-${index + 1}`),
    performedAt,
    title: cleanText(rawEntry.title) || null,
    sets,
  }
}

function normalizeWatchExerciseStatistics(rawStatistics) {
  if (!rawStatistics || typeof rawStatistics !== "object" || Array.isArray(rawStatistics)) return null

  const lastSets = (Array.isArray(rawStatistics.lastSets) ? rawStatistics.lastSets : [])
    .slice(0, MAX_WATCH_LAST_SETS)
    .map(normalizeWatchPerformanceSet)
  const recentSessions = (Array.isArray(rawStatistics.recentSessions) ? rawStatistics.recentSessions : [])
    .slice(0, MAX_WATCH_RECENT_SESSIONS)
    .map(normalizeWatchExerciseHistoryEntry)
    .filter(Boolean)
  const statistics = {
    sessionCount: boundedCount(rawStatistics.sessionCount),
    setCount: boundedCount(rawStatistics.setCount),
    lastPerformedAt: safeIsoDate(rawStatistics.lastPerformedAt),
    lastSets,
    recentSessions,
    bestWeightKg: boundedPositiveNumber(rawStatistics.bestWeightKg, 2000),
    bestWeightReps: boundedPositiveNumber(rawStatistics.bestWeightReps, 10000, true),
    bestReps: boundedPositiveNumber(rawStatistics.bestReps, 10000, true),
    bestDurationSeconds: boundedPositiveNumber(rawStatistics.bestDurationSeconds, 86400, true),
    bestDistanceMeters: boundedPositiveNumber(rawStatistics.bestDistanceMeters, 10000000),
  }

  const hasHistory =
    statistics.sessionCount > 0 ||
    statistics.setCount > 0 ||
    statistics.lastSets.length > 0 ||
    statistics.recentSessions.length > 0 ||
    statistics.bestWeightKg !== null ||
    statistics.bestReps !== null ||
    statistics.bestDurationSeconds !== null ||
    statistics.bestDistanceMeters !== null
  return hasHistory ? statistics : null
}

function watchStatisticsByExerciseId(rawStatistics) {
  const result = new Map()
  const entries = Array.isArray(rawStatistics) ? rawStatistics : []
  entries.forEach((entry) => {
    const exerciseId = idString(entry?.exerciseId)
    const statistics = normalizeWatchExerciseStatistics(entry)
    if (exerciseId && statistics) result.set(exerciseId, statistics)
  })
  return result
}

function buildWatchExerciseLibrary(rawExerciseLibrary, statisticsByExerciseId, excludedExerciseIds = new Set()) {
  const result = []
  const seen = new Set()
  const entries = Array.isArray(rawExerciseLibrary) ? rawExerciseLibrary : []

  entries.slice(0, MAX_WATCH_STATISTIC_EXERCISES).forEach((entry) => {
    const exerciseId = idString(entry?.exerciseId || entry?._id)
    const statistics = statisticsByExerciseId.get(exerciseId)
    if (!exerciseId || !statistics || seen.has(exerciseId) || excludedExerciseIds.has(exerciseId)) return

    seen.add(exerciseId)
    result.push({
      id: exerciseId,
      instanceId: `history:${exerciseId}`,
      parentExerciseId: null,
      title: cleanText(entry?.title, "Übung"),
      subtitle: null,
      imageUrl: watchExerciseImageUrl(entry),
      statistics,
      sets: [],
    })
  })

  return result
}

function collectWatchExerciseIds(workout) {
  const ids = new Set()
  const append = (exercise) => {
    const exerciseId = idString(exercise?._id || exercise?.exercise_id)
    if (exerciseId && ids.size < MAX_WATCH_STATISTIC_EXERCISES) ids.add(exerciseId)
  }

  const splits = Array.isArray(workout?.splits?.workouts) ? workout.splits.workouts : []
  splits.slice(0, MAX_WATCH_PLANS).forEach((split) => {
    const exercises = Array.isArray(split?.exercises) ? split.exercises : []
    exercises.slice(0, MAX_EXERCISES_PER_PLAN).forEach((exercise) => {
      if (exercise?.isSuperset && Array.isArray(exercise.exercises)) {
        exercise.exercises.forEach(append)
      } else {
        append(exercise)
      }
    })
  })
  return Array.from(ids)
}

function settingList(rawSet) {
  if (Array.isArray(rawSet)) return rawSet
  if (rawSet && Array.isArray(rawSet.settings)) return rawSet.settings
  return []
}

function findSetting(settings, matcher) {
  return settings.find((setting) => matcher(String(setting?.text || "").toLowerCase()))
}

function convertDurationToSeconds(setting) {
  if (!setting) return null
  const value = finiteNumber(setting.value)
  if (value === null) return null

  const unit = String(setting.unit || setting.durationUnit || "min").toLowerCase()
  if (unit.includes("min")) return Math.max(0, Math.round(value * 60))
  if (unit === "h" || unit.includes("stunde")) return Math.max(0, Math.round(value * 3600))
  return Math.max(0, Math.round(value))
}

function convertDistanceToMeters(setting) {
  if (!setting) return null
  const value = finiteNumber(setting.value)
  if (value === null) return null

  const unit = String(setting.unit || "").toLowerCase()
  if (unit === "km" || unit.includes("kilometer")) return Math.max(0, value * 1000)
  return Math.max(0, value)
}

function mapTargetSet(rawSet, targetId, position) {
  const settings = settingList(rawSet)
  const exactRepsSetting = findSetting(settings, (text) => text === "wiederholungen")
  const minimumRepsSetting = findSetting(settings, (text) => text.includes("min wiederhol"))
  const maximumRepsSetting = findSetting(settings, (text) => text.includes("max wiederhol"))
  const weightSetting = findSetting(settings, (text) => text === "gewicht" || text.includes("gewicht"))
  const durationSetting = findSetting(settings, (text) => text === "dauer" || text.includes("dauer"))
  const distanceSetting = findSetting(settings, (text) => text === "distanz" || text.includes("distanz"))
  const restSetting = findSetting(settings, (text) => text === "pause" || text.includes("satzpause"))

  const exactReps = finiteNumber(exactRepsSetting?.value)
  const minimumReps = finiteNumber(minimumRepsSetting?.value)
  const maximumReps = finiteNumber(maximumRepsSetting?.value)
  const reps =
    exactReps !== null
      ? exactReps
      : minimumReps !== null && maximumReps !== null
        ? Math.round((minimumReps + maximumReps) / 2)
        : minimumReps ?? maximumReps
  const weight = finiteNumber(weightSetting?.value)

  return {
    id: targetId,
    position,
    targetReps: reps === null ? null : Math.max(0, Math.round(reps)),
    targetWeightKg: weight === null ? null : Math.max(0, weight),
    targetDurationSeconds: convertDurationToSeconds(durationSetting),
    targetDistanceMeters: convertDistanceToMeters(distanceSetting),
    targetRestSeconds: boundedPositiveNumber(restSetting?.value, 15 * 60, true),
  }
}

function plannedSets(exercise) {
  const sets = []
  if (Array.isArray(exercise?.warmup) && exercise.warmup.length > 0) {
    sets.push(exercise.warmup)
  }
  if (Array.isArray(exercise?.sets)) {
    sets.push(...exercise.sets)
  }
  return sets.length > 0 ? sets.slice(0, MAX_SETS_PER_EXERCISE) : [[]]
}

function normalExercise(planId, exercise, exerciseIndex, statisticsByExerciseId) {
  const instanceId = `${planId}:exercise:${exerciseIndex}`
  const exerciseId = idString(exercise?._id || exercise?.exercise_id) || instanceId
  const sets = plannedSets(exercise).map((set, setIndex) =>
    mapTargetSet(set, `${instanceId}:set:${setIndex}`, setIndex + 1),
  )

  return {
    id: exerciseId,
    instanceId,
    parentExerciseId: null,
    title: cleanText(exercise?.title, `Übung ${exerciseIndex + 1}`),
    subtitle: cleanText(exercise?.subtitle || exercise?.userNote) || null,
    imageUrl: watchExerciseImageUrl(exercise),
    statistics: statisticsByExerciseId.get(exerciseId) || null,
    sets,
  }
}

function supersetExercises(planId, exercise, exerciseIndex, statisticsByExerciseId) {
  const parentExerciseId = idString(exercise?.supersetId || exercise?._id) || `${planId}:superset:${exerciseIndex}`
  const subExercises = Array.isArray(exercise?.exercises) ? exercise.exercises : []
  const prepared = subExercises.map((subExercise) => ({
    subExercise,
    sets: plannedSets(subExercise),
  }))
  const roundCount = Math.max(0, ...prepared.map((entry) => entry.sets.length))
  const result = []

  for (let roundIndex = 0; roundIndex < roundCount; roundIndex += 1) {
    prepared.forEach(({ subExercise, sets }, subIndex) => {
      const rawSet = sets[roundIndex]
      if (!rawSet) return

      const instanceId = `${planId}:superset:${exerciseIndex}:round:${roundIndex}:exercise:${subIndex}`
      const exerciseId = idString(subExercise?._id || subExercise?.exercise_id) || instanceId
      result.push({
        id: exerciseId,
        instanceId,
        parentExerciseId,
        title: cleanText(subExercise?.title, `Supersatz ${subIndex + 1}`),
        subtitle: `Supersatz · Runde ${roundIndex + 1}`,
        imageUrl: watchExerciseImageUrl(subExercise),
        statistics: statisticsByExerciseId.get(exerciseId) || null,
        sets: [mapTargetSet(rawSet, `${instanceId}:set:0`, 1)],
      })
    })
  }

  return result
}

function watchExercises(planId, split, statisticsByExerciseId) {
  const exercises = Array.isArray(split?.exercises) ? split.exercises : []
  const mapped = []

  exercises.forEach((exercise, exerciseIndex) => {
    if (exercise?.isSuperset) {
      mapped.push(...supersetExercises(planId, exercise, exerciseIndex, statisticsByExerciseId))
    } else {
      mapped.push(normalExercise(planId, exercise, exerciseIndex, statisticsByExerciseId))
    }
  })

  return mapped.slice(0, MAX_EXERCISES_PER_PLAN)
}

export function buildLinkedWatchExercises(exercise, requestId) {
  return watchExercises(`added:${requestId}`, { exercises: [exercise] }, new Map())
}

export function buildWatchPlanSnapshot(
  workout,
  userId,
  exerciseStatistics = [],
  rawExerciseLibrary = [],
  rawGroupFitnessActivities = [],
) {
  const workoutId = idString(workout?._id)
  const splits = Array.isArray(workout?.splits?.workouts) ? workout.splits.workouts : []
  const revisionBase = cleanText(workout?.updatedAt || workout?.ts || workoutId)
  const statisticsByExerciseId = watchStatisticsByExerciseId(exerciseStatistics)

  const plans = splits
    .slice(0, MAX_WATCH_PLANS)
    .map((split, splitIndex) => {
      const splitKey = idString(split?._id || split?.id) || `split-${splitIndex + 1}`
      const planId = `${workoutId}:${splitKey}`
      const exercises = watchExercises(planId, split, statisticsByExerciseId)
      if (exercises.length === 0) return null

      const color = String(split?.fixedVisualization?.color || "").trim()
      return {
        id: planId,
        workoutId,
        splitKey,
        revision: `${revisionBase}:${splitKey}`,
        title: cleanText(split?.title, workout?.title || `Training ${splitIndex + 1}`),
        subtitle: cleanText(workout?.title) || null,
        colorHex: /^#[0-9a-f]{6}$/i.test(color) ? color : null,
        iconUrl: watchPlanIconUrl(split),
        exercises,
      }
    })
    .filter(Boolean)
  const planExerciseIds = new Set(
    plans.flatMap((plan) => plan.exercises.map((exercise) => exercise.id)),
  )
  const exerciseLibrary = buildWatchExerciseLibrary(
    rawExerciseLibrary,
    statisticsByExerciseId,
    planExerciseIds,
  )
  const groupFitnessActivities = rawGroupFitnessActivities === null
    ? null
    : (Array.isArray(rawGroupFitnessActivities) ? rawGroupFitnessActivities : [])
        .slice(0, MAX_WATCH_GROUP_FITNESS_ACTIVITIES)
        .map(normalizeWatchGroupFitnessActivity)
        .filter(Boolean)
        .sort((left, right) => new Date(left.startAt).getTime() - new Date(right.startAt).getTime())

  return {
    schemaVersion: WATCH_SCHEMA_VERSION,
    generatedAt: isoDateWithoutMilliseconds(),
    userId: String(userId),
    plans,
    exerciseLibrary,
    ...(groupFitnessActivities === null ? {} : { groupFitnessActivities }),
  }
}

class WatchSyncService {
  constructor() {
    this.started = false
    this.syncTimer = null
    this.companionStartTimeout = null
    this.unsubscribeStore = null
    this.inFlightSessions = new Set()
    this.lastObservedUserId = null
    this.connection = Vue.observable({ status: null })
    this.connectionRevision = 0

    this.handlePlanRequest = this.handlePlanRequest.bind(this)
    this.handleCompletedSession = this.handleCompletedSession.bind(this)
    this.handleCompanionMetrics = this.handleCompanionMetrics.bind(this)
    this.handleOnline = this.handleOnline.bind(this)
    this.handlePlatformReady = this.handlePlatformReady.bind(this)
    this.handleConnectionChanged = (event) => this.setConnectionStatus(event?.detail)
    this.handleVisible = () => { if (document.visibilityState === "visible") this.refreshConnection() }
  }

  hasCompanionWatch() {
    const status = this.connection.status
    // On iOS an unopened Watch app is not reachable yet, but can be launched.
    return this.isAvailable() && status?.supported === true && status?.activated === true &&
      status?.paired === true && status?.watchAppInstalled === true &&
      (currentWatchPlatform() !== "wear_os" || status?.reachable === true)
  }

  setConnectionStatus(status) {
    this.connectionRevision++
    this.connection.status = this.isAvailable() ? status || null : null
  }

  async refreshConnection() {
    if (!this.isAvailable()) { this.setConnectionStatus(null); return null }
    const revision = this.connectionRevision
    try {
      const state = await this.callNative("getWatchBridgeState")
      // A live disconnect event takes precedence over an older snapshot response.
      if (revision === this.connectionRevision) this.setConnectionStatus(state?.status)
      return state
    } catch (_) {
      if (revision === this.connectionRevision) this.setConnectionStatus(null)
      return null
    }
  }

  isAvailable() {
    return trainingFeatureAccess.isAllowed() && this.hasNativeBridge()
  }

  hasNativeBridge() {
    const isNativeApp = ["isIOS", "isAndroid"].some((key) => localStorage.getItem(key) === "true") ||
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    return Boolean(isNativeApp && window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === "function")
  }

  start() {
    if (this.started) return
    this.started = true
    this.unsubscribeAccess = trainingFeatureAccess.subscribe(() => this.bootstrap())
    this.lastObservedUserId = userService.getUserId()
    watchDiagnostic("service_started", { bridgeAvailable: this.isAvailable() })

    window.addEventListener("watchPlanSnapshotRequested", this.handlePlanRequest)
    window.addEventListener("watchCompletedSession", this.handleCompletedSession)
    window.addEventListener("watchCompanionMetrics", this.handleCompanionMetrics)
    window.addEventListener("watchConnectionChanged", this.handleConnectionChanged)
    window.addEventListener("trainingLiveForeground", this.handleVisible)
    document.addEventListener("visibilitychange", this.handleVisible)
    window.addEventListener("online", this.handleOnline)
    window.addEventListener("flutterInAppWebViewPlatformReady", this.handlePlatformReady)

    this.unsubscribeStore = store.subscribe((mutation) => {
      if (mutation.type === "userStore/setUser") {
        const userId = userService.getUserId()
        const changedUser = userId !== this.lastObservedUserId
        this.lastObservedUserId = userId
        if (changedUser) this.bootstrap()
      }

      if (["runWorkoutStore/MERGE_HUB_DATA", "runWorkoutStore/SET_HUB_DATA"].includes(mutation.type)) {
        this.schedulePlanSync()
      }
    })

    this.bootstrap()
  }

  stop() {
    if (!this.started) return
    this.started = false
    this.unsubscribeAccess?.(); this.unsubscribeAccess = null
    window.removeEventListener("watchPlanSnapshotRequested", this.handlePlanRequest)
    window.removeEventListener("watchCompletedSession", this.handleCompletedSession)
    window.removeEventListener("watchCompanionMetrics", this.handleCompanionMetrics)
    window.removeEventListener("watchConnectionChanged", this.handleConnectionChanged)
    window.removeEventListener("trainingLiveForeground", this.handleVisible)
    document.removeEventListener("visibilitychange", this.handleVisible)
    window.removeEventListener("online", this.handleOnline)
    window.removeEventListener("flutterInAppWebViewPlatformReady", this.handlePlatformReady)
    if (this.unsubscribeStore) this.unsubscribeStore()
    if (this.syncTimer) clearTimeout(this.syncTimer)
    if (this.companionStartTimeout) clearTimeout(this.companionStartTimeout)
    this.unsubscribeStore = null
    this.syncTimer = null
    this.companionStartTimeout = null
    this.setConnectionStatus(null)
  }

  async bootstrap() {
    if (this.started && !trainingFeatureAccess.isAllowed()) {
      this.setConnectionStatus(null)
      if (this.syncTimer) clearTimeout(this.syncTimer)
      if (this.companionStartTimeout) clearTimeout(this.companionStartTimeout)
      this.syncTimer = null; this.companionStartTimeout = null
      if (trainingFeatureAccess.state.ready) {
        store.commit("runWorkoutStore/RESET_WATCH_COMPANION_STATE")
        await this.clearUserData()
      }
      return
    }
    if (!this.started || !this.isAvailable()) {
      watchDiagnostic("bootstrap_skipped", {
        started: this.started,
        bridgeAvailable: this.isAvailable(),
      })
      return
    }

    try {
      watchDiagnostic("bootstrap_started")
      const state = await this.refreshConnection()
      const pendingSessions = Array.isArray(state?.pendingSessions) ? state.pendingSessions : []
      watchDiagnostic("native_state_received", {
        activated: state?.status?.activated === true,
        paired: state?.status?.paired === true,
        watchAppInstalled: state?.status?.watchAppInstalled === true,
        reachable: state?.status?.reachable === true,
        pendingSessionCount: pendingSessions.length,
      })
      for (const pending of pendingSessions) {
        if (!pending?.payload) continue
        try {
          await this.saveCompletedSession(JSON.parse(pending.payload))
        } catch (error) {
          console.warn("Gespeicherte Watch-Session konnte nicht gelesen werden.", error?.message)
        }
      }

      const userId = userService.getUserId()
      if (userId) await this.syncPlans()
      else {
        watchDiagnostic("no_signed_in_user")
        await this.callNative("clearWatchData")
      }
    } catch (error) {
      watchDiagnostic("bootstrap_failed", watchErrorDetails(error))
      console.warn("Watch-Synchronisierung konnte nicht gestartet werden.", error?.message)
    }
  }

  schedulePlanSync() {
    if (!this.isAvailable()) return
    if (this.syncTimer) clearTimeout(this.syncTimer)
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null
      this.syncPlans().catch((error) => console.warn("Watch-Synchronisierung nicht möglich.", error?.message))
    }, 750)
  }

  handlePlanRequest() {
    watchDiagnostic("plan_request_received")
    this.syncPlans().catch((error) => {
      watchDiagnostic("plan_request_failed", watchErrorDetails(error))
      console.warn("Watch-Planabruf ist fehlgeschlagen.", error?.message)
    })
  }

  handleOnline() {
    this.bootstrap()
  }

  handlePlatformReady() {
    this.bootstrap()
  }

  handleCompletedSession(event) {
    this.saveCompletedSession(event?.detail)
  }

  handleCompanionMetrics(event) {
    if (!trainingFeatureAccess.isAllowed()) return
    const metrics = event?.detail
    if (!metrics || typeof metrics !== "object") return

    const clientSessionId = String(metrics.clientSessionId || "").toLowerCase()
    const activeSessionId = String(
      store.state.runWorkoutStore?.watchCompanion?.clientSessionId || "",
    ).toLowerCase()
    if (!clientSessionId || !activeSessionId || clientSessionId !== activeSessionId) return

    const pendingStatuses = ["starting", "waiting_for_sensor", "open_watch"]
    if (this.companionStartTimeout && !pendingStatuses.includes(metrics.status)) {
      clearTimeout(this.companionStartTimeout)
      this.companionStartTimeout = null
    }

    store.commit("runWorkoutStore/SET_WATCH_COMPANION_STATE", {
      ...metrics,
      clientSessionId,
      error: null,
    })
  }

  async startPhoneCompanionWorkout({
    automatic = false,
    title = "Training",
    workoutId = null,
    splitKey = null,
    planRevision = null,
    startedAt = null,
    healthActivityType = "traditionalStrengthTraining",
  } = {}) {
    if (!this.isAvailable()) {
      throw new Error("Das Watch-Pulstracking ist nur in der Roody App verfügbar.")
    }
    const userId = userService.getUserId()
    if (!userId) throw new Error("Bitte zuerst anmelden.")
    const access = trainingFeatureAccess.capture()

    const live = trainingLiveService.ensureSession()
    const isCurrentSession = () => {
      const workout = store.state.runWorkoutStore
      const current = workout?.liveSession
      return trainingFeatureAccess.isCurrent(access) &&
        Boolean(workout?.activeSplit) && current?.clientSessionId === live?.clientSessionId &&
        current?.userId === String(userId) && current?.owner !== "watch" &&
        !["ending", "completed", "discarded"].includes(current?.phase) && !current?.savedAt && !current?.pendingSave
    }
    if (!live?.clientSessionId || !isCurrentSession() || !this.hasCompanionWatch()) return null
    const sessionId = String(live.clientSessionId).toLowerCase()
    const companion = store.state.runWorkoutStore.watchCompanion || {}
    if (String(companion.clientSessionId || "").toLowerCase() === sessionId && (
      (automatic && companion.status !== "idle") ||
      ["starting", "waiting_for_sensor", "open_watch", "running", "paused", "stopping", "discarding"].includes(companion.status)
    )) return null
    const command = {
      action: "start",
      platform: currentWatchPlatform(),
      clientSessionId: sessionId,
      userId: String(userId),
      title: cleanText(title, "Training"),
      workoutId: idString(workoutId) || null,
      splitKey: cleanText(splitKey) || null,
      planRevision: cleanText(planRevision) || null,
      startedAt: safeIsoDate(startedAt) || isoDateWithoutMilliseconds(),
      healthActivityType,
    }

    store.commit("runWorkoutStore/SET_WATCH_COMPANION_STATE", {
      clientSessionId: sessionId,
      platform: currentWatchPlatform(),
      status: "starting",
      error: null,
    })

    try {
      // Publish the phone's current session before asking the Watch to open it.
      trainingLiveService.schedule()
      await Promise.resolve()
      await trainingLiveService.queue
      if (!isCurrentSession()) return null
      if (!this.hasCompanionWatch()) {
        store.commit("runWorkoutStore/RESET_WATCH_COMPANION_STATE")
        return null
      }
      const response = await this.callNative("watchCompanionWorkoutCommand", command)
      if (!isCurrentSession()) return null
      if (response?.accepted !== true) {
        throw new Error(response?.error || "Die Watch hat den Start nicht bestätigt.")
      }
      const currentCompanion = store.state.runWorkoutStore?.watchCompanion || {}
      if (currentCompanion.status === "starting") {
        store.commit("runWorkoutStore/SET_WATCH_COMPANION_STATE", {
          status: "waiting_for_sensor",
        })
      }
      if (this.companionStartTimeout) clearTimeout(this.companionStartTimeout)
      if (["starting", "waiting_for_sensor", "open_watch"].includes(
        store.state.runWorkoutStore?.watchCompanion?.status,
      )) {
        this.companionStartTimeout = setTimeout(() => {
          const companion = store.state.runWorkoutStore?.watchCompanion || {}
          if (
            isCurrentSession() &&
            String(companion.clientSessionId || "").toLowerCase() === sessionId &&
            ["starting", "waiting_for_sensor", "open_watch"].includes(companion.status)
          ) {
            store.commit("runWorkoutStore/SET_WATCH_COMPANION_STATE", {
              status: "error",
              error: "Keine Rückmeldung der Watch. Öffne Roody auf der Watch und versuche es erneut.",
            })
          }
          this.companionStartTimeout = null
        }, 45000)
      }
      return sessionId
    } catch (error) {
      if (!isCurrentSession()) return null
      store.commit("runWorkoutStore/SET_WATCH_COMPANION_STATE", {
        status: "error",
        error: error?.message || "Watch-Pulstracking konnte nicht gestartet werden.",
      })
      throw error
    }
  }

  async stopPhoneCompanionWorkout({ discard = false } = {}) {
    const companion = store.state.runWorkoutStore?.watchCompanion || {}
    const clientSessionId = String(companion.clientSessionId || "").toLowerCase()
    if (!clientSessionId || !this.isAvailable()) return null

    if (this.companionStartTimeout) {
      clearTimeout(this.companionStartTimeout)
      this.companionStartTimeout = null
    }

    store.commit("runWorkoutStore/SET_WATCH_COMPANION_STATE", {
      status: discard ? "discarding" : "stopping",
      error: null,
    })
    try {
      const response = await this.callNative("watchCompanionWorkoutCommand", {
        action: discard ? "discard" : "stop",
        clientSessionId,
      })
      if (response?.accepted !== true) {
        throw new Error(response?.error || "Die Watch hat den Stopp nicht bestätigt.")
      }
      return clientSessionId
    } catch (error) {
      store.commit("runWorkoutStore/SET_WATCH_COMPANION_STATE", {
        status: "error",
        error: error?.message || "Watch-Pulstracking konnte nicht beendet werden.",
      })
      throw error
    }
  }

  createClientSessionId() {
    if (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function") {
      return globalThis.crypto.randomUUID()
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
      const random = Math.floor(Math.random() * 16)
      const value = character === "x" ? random : (random & 0x3) | 0x8
      return value.toString(16)
    })
  }

  async clearUserData() {
    this.lastObservedUserId = null
    if (!this.hasNativeBridge()) return

    try {
      await this.callNative("clearWatchData")
    } catch (error) {
      console.warn("Watch-Benutzerdaten konnten nicht entfernt werden.", error?.message)
    }
  }

  async syncPlans() {
    if (!this.started || !this.isAvailable()) {
      watchDiagnostic("plan_sync_skipped", {
        started: this.started,
        bridgeAvailable: this.isAvailable(),
      })
      return
    }

    watchDiagnostic("plan_sync_started")

    const userId = userService.getUserId()
    const access = trainingFeatureAccess.capture()
    if (!userId) {
      watchDiagnostic("plan_sync_without_user")
      await this.callNative("clearWatchData")
      return
    }

    let workout = null
    try {
      workout = await api.fetchOwnWorkout()
    } catch (error) {
      if (error?.response?.status !== 404) throw error
    }
    if (!trainingFeatureAccess.isCurrent(access)) return
    watchDiagnostic("workout_loaded", { hasWorkout: Boolean(workout) })

    let exerciseStatistics = []
    let exerciseLibrary = []
    let groupFitnessActivities = null
    const exerciseIds = collectWatchExerciseIds(workout)
    try {
      const response = await api.workoutApi.fetchWatchExerciseStatistics(exerciseIds, true)
      exerciseStatistics = Array.isArray(response?.statistics) ? response.statistics : []
      exerciseLibrary = Array.isArray(response?.exerciseLibrary) ? response.exerciseLibrary : []
      watchDiagnostic("exercise_statistics_loaded", {
        requestedCount: exerciseIds.length,
        receivedCount: exerciseStatistics.length,
        libraryCount: exerciseLibrary.length,
      })
    } catch (error) {
      watchDiagnostic("exercise_statistics_failed", watchErrorDetails(error))
      console.warn("Watch-Übungsstatistiken konnten nicht aktualisiert werden.", error?.response?.status || error?.message)
      if (await trainingFeatureAccess.reject(access, error)) return
    }
    if (!trainingFeatureAccess.isCurrent(access)) return

    try {
      const response = await api.workoutApi.fetchWatchGroupFitnessActivities()
      groupFitnessActivities = Array.isArray(response?.activities) ? response.activities : []
      watchDiagnostic("group_fitness_activities_loaded", {
        activityCount: groupFitnessActivities.length,
      })
    } catch (error) {
      watchDiagnostic("group_fitness_activities_failed", watchErrorDetails(error))
      console.warn("Watch-Groupfitness-Kurse konnten nicht aktualisiert werden.", error?.response?.status || error?.message)
      if (await trainingFeatureAccess.reject(access, error)) return
    }
    if (!trainingFeatureAccess.isCurrent(access)) return

    const snapshot = buildWatchPlanSnapshot(
      workout,
      userId,
      exerciseStatistics,
      exerciseLibrary,
      groupFitnessActivities,
    )
    watchDiagnostic("snapshot_built", {
      planCount: snapshot.plans.length,
      exerciseCount: snapshot.plans.reduce((sum, plan) => sum + plan.exercises.length, 0),
      libraryCount: snapshot.exerciseLibrary.length,
      groupFitnessCount: Array.isArray(snapshot.groupFitnessActivities)
        ? snapshot.groupFitnessActivities.length
        : -1,
    })
    const response = await this.callNative("syncWatchPlans", snapshot)
    if (response?.accepted === false) {
      throw new Error(response.error || "Watch-Plan wurde nicht angenommen.")
    }
    watchDiagnostic("snapshot_accepted_by_native", {
      accepted: response?.accepted !== false,
    })
  }

  async saveCompletedSession(session) {
    const access = trainingFeatureAccess.capture()
    if (access === null) return
    if (!session || Number(session.schemaVersion) !== WATCH_SCHEMA_VERSION) {
      watchDiagnostic("session_ignored_schema")
      return
    }

    const clientSessionId = String(session.clientSessionId || "").toLowerCase()
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(clientSessionId)) {
      watchDiagnostic("session_ignored_identifier")
      return
    }
    if (this.inFlightSessions.has(clientSessionId)) {
      watchDiagnostic("session_upload_already_running")
      return
    }

    const currentUserId = userService.getUserId()
    if (!currentUserId) {
      watchDiagnostic("session_waiting_for_login")
      await this.reportSessionFailure(clientSessionId, "Bitte zuerst in der Handy-App anmelden.")
      return
    }
    if (String(session.userId) !== String(currentUserId)) {
      watchDiagnostic("session_user_mismatch")
      await this.reportSessionFailure(
        clientSessionId,
        "Auf Handy und Watch ist nicht derselbe Benutzer angemeldet.",
      )
      return
    }

    this.inFlightSessions.add(clientSessionId)
    try {
      watchDiagnostic("session_upload_started")
      const savedSession = await api.workoutApi.saveWatchWorkoutSession({ session })
      if (!trainingFeatureAccess.isCurrent(access)) return
      if (
        savedSession?.status !== "saved" ||
        String(savedSession?.clientSessionId || "").toLowerCase() !== clientSessionId
      ) {
        throw new Error("Der Server hat das Watch-Training nicht eindeutig bestätigt.")
      }
      watchDiagnostic("session_saved_by_server", {
        saved: true,
      })
      // The calendar can refresh as soon as the server saved the session,
      // even if delivery of the acknowledgement to the Watch is delayed.
      try {
        window.dispatchEvent(new CustomEvent("roody:watch-session-saved", {
          detail: { userId: String(currentUserId), clientSessionId },
        }))
      } catch (_) {
        // A UI refresh must not interrupt acknowledgement of a saved workout.
      }
      const acknowledgement = await this.callNative("acknowledgeWatchSessions", [clientSessionId])
      if (acknowledgement?.accepted !== true) {
        throw new Error(acknowledgement?.error || "Watch-Bestätigung fehlgeschlagen.")
      }
      watchDiagnostic("session_acknowledgement_queued")

      Promise.resolve(store.dispatch("runWorkoutStore/loadHubData")).catch((error) => {
        watchDiagnostic("session_hub_refresh_failed", watchErrorDetails(error))
      })
      this.schedulePlanSync()
    } catch (error) {
      watchDiagnostic("session_upload_failed", watchErrorDetails(error))
      await trainingFeatureAccess.reject(access, error)
      await this.reportSessionFailure(clientSessionId, watchTransferErrorMessage(error))
      console.warn("Watch-Training bleibt für einen erneuten Versuch gespeichert.", error?.response?.status || error?.message)
    } finally {
      this.inFlightSessions.delete(clientSessionId)
    }
  }

  async reportSessionFailure(clientSessionId, message) {
    try {
      const response = await this.callNative("reportWatchSessionFailure", {
        clientSessionId,
        message: String(message || "Übertragung fehlgeschlagen.").slice(0, 240),
      })
      watchDiagnostic("session_failure_reported", {
        accepted: response?.accepted === true,
      })
    } catch (error) {
      watchDiagnostic("session_failure_report_failed", watchErrorDetails(error))
    }
  }

  async callNative(handlerName, argument) {
    const cleanup = handlerName === "clearWatchData"
    const access = trainingFeatureAccess.capture()
    if (!this.hasNativeBridge() || (access === null && !cleanup)) return null
    const result = await (argument === undefined
      ? window.flutter_inappwebview.callHandler(handlerName)
      : window.flutter_inappwebview.callHandler(handlerName, argument))
    return cleanup || trainingFeatureAccess.isCurrent(access) ? result : null
  }
}

export default new WatchSyncService()
